import {
	isFailedSubagentStatus,
	isTerminalSubagentStatus,
	isTerminalWorkflowStatus,
	type ChatSubagentRun,
	type ChatWorkflowRun,
	type SubagentRunDetail,
} from "./chat";

/**
 * ヘッダーに使う run を選ぶ。実行履歴の取得が失敗している間は detail が古いまま止まるため、
 * そのときは一覧の run を正とする (終了済みの run を「実行中」のまま描き続けないように)。
 * どちらか一方しか無い場合はあるほうを使う — 一覧に行が無い run でも実行履歴だけで描けるようにする。
 */
export function resolveDisplayedSubagentRun(
	listRun: ChatSubagentRun | undefined,
	detail: SubagentRunDetail | null,
	error: boolean,
): ChatSubagentRun | undefined {
	if (!error) return detail?.run ?? listRun;
	// 取得失敗中に detail から要約の全文を拾い直す余地は無い。summary は terminal status を書くのと
	// 同じ UPDATE でしか入らず (SubagentRunStore.TrySetTerminalAsync)、その terminal を取得できた
	// 時点でポーリングは止まり error も下りる。つまり「error 中の detail が全文を持つ」は成立しない。
	return listRun ?? detail?.run;
}

/**
 * ポーリングで届いた差分を取得済みの実行履歴へ連結する。実行履歴は追記専用なので messages は
 * 後ろへ足すだけ。run は常に最新へ差し替える — ツールが 1 件で数分かかる間はメッセージが増えず
 * カウンタだけが進むため、ここで据え置くと詳細の進捗が一覧と食い違ったまま止まる。
 */
export function mergeSubagentTranscript(
	previous: SubagentRunDetail | null,
	next: SubagentRunDetail,
): SubagentRunDetail {
	if (previous === null || previous.run.id !== next.run.id) return next;
	// 差分が無いときは messages の参照を保ち、実行履歴の再構築を避ける。
	if (next.messages.length === 0) return { run: next.run, messages: previous.messages };
	return { run: next.run, messages: [...previous.messages, ...next.messages] };
}

/** 実行中と実行済みに分ける。実行中は開始が古い順、実行済みは終了が新しい順。 */
export function splitSubagentRuns(runs: ChatSubagentRun[]): {
	running: ChatSubagentRun[];
	finished: ChatSubagentRun[];
} {
	const running = runs
		.filter((run) => !isTerminalSubagentStatus(run.status))
		.sort((a, b) => a.startedAt.localeCompare(b.startedAt));
	const finished = runs
		.filter((run) => isTerminalSubagentStatus(run.status))
		.sort((a, b) => (b.endedAt ?? b.startedAt).localeCompare(a.endedAt ?? a.startedAt));
	return { running, finished };
}

export interface SubagentPhaseGroup {
	/** 同名フェーズを回ごとに分けるための一意キー。表示には使わない。 */
	key: string;
	title: string;
	runs: ChatSubagentRun[];
	completed: number;
	total: number;
	hasActive: boolean;
	/**
	 * 異常終了した run を含むか (ユーザーによる停止は除く)。見出しは completed/total しか出さず、
	 * 全滅したフェーズも「3/3」と読めてしまうため、カウントバッジの配色にこれを使う。
	 */
	hasFailure: boolean;
}

/**
 * phase 名は回ごとに再利用されるため、所有者と組でキーにする。所有者は workflow (あれば) か
 * 親ジョブ。区切りは phase 名に現れない文字。
 */
function phaseGroupKey(owner: string, phase: string): string {
	return `${owner}\u0000${phase}`;
}

/** 実行中と実行済みに分ける。並びは {@link splitSubagentRuns} と同じ。 */
export function splitWorkflowRuns(workflowRuns: readonly ChatWorkflowRun[]): {
	running: ChatWorkflowRun[];
	finished: ChatWorkflowRun[];
} {
	const running = workflowRuns
		.filter((run) => !isTerminalWorkflowStatus(run.status))
		.sort((a, b) => a.startedAt.localeCompare(b.startedAt));
	const finished = workflowRuns
		.filter((run) => isTerminalWorkflowStatus(run.status))
		.sort((a, b) => (b.endedAt ?? b.startedAt).localeCompare(a.endedAt ?? a.startedAt));
	return { running, finished };
}

/**
 * run_subagent が phase 付きで起動した run をフェーズ単位へ集約する。フェーズが 1 つでも階層化する。
 * 同時実行は profile ごとの上限で頭打ちになる (SubagentProfile.MaxActiveBackgroundRuns) ため親は段階を
 * 順に投げることになり、2 フェーズ目が現れるまで待つ設計だと最初のフェーズが走っている間ずっと
 * フェーズ表示が出ない。
 *
 * workflow が所有する run は、その workflow のカード (WorkflowTaskCard) が宣言ノードと突き合わせて描く
 * ので、ここでは除く。ただし workflow 行が届いていない (一覧取得の失敗) 間はカードが無いので、
 * 所有者を workflow id にしてここで見せる — でないと走っている run がどこにも出ない。
 *
 * 並びは「フェーズの最初の run の createdAt」。startedAt ではなく createdAt を使うのは、background の
 * startedAt が claim 時に書き換わり、これを基準にするとフェーズの順が後から入れ替わるため。
 */
export function groupSubagentRunsByPhase(
	runs: ChatSubagentRun[],
	workflowRuns: readonly ChatWorkflowRun[] = [],
): {
	phases: SubagentPhaseGroup[];
	running: ChatSubagentRun[];
	finished: ChatSubagentRun[];
} {
	const workflowIds = new Set(workflowRuns.map((workflow) => workflow.id));
	const standalone = runs.filter(
		(run) => run.workflowRunId === null || !workflowIds.has(run.workflowRunId),
	);
	const byPhase = new Map<string, { title: string; runs: ChatSubagentRun[] }>();
	for (const run of standalone) {
		if (!run.phase) continue;
		const key = phaseGroupKey(run.workflowRunId ?? run.parentJobId, run.phase);
		const group = byPhase.get(key) ?? { title: run.phase, runs: [] };
		group.runs.push(run);
		byPhase.set(key, group);
	}

	const phases = [...byPhase.entries()]
		.map(([key, { title, runs: phaseRuns }]) => {
			const ordered = [...phaseRuns].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
			const completed = ordered.filter((run) => isTerminalSubagentStatus(run.status)).length;
			return {
				sortKey: ordered[0].createdAt,
				group: {
					key,
					title,
					runs: ordered,
					completed,
					total: ordered.length,
					hasActive: completed < ordered.length,
					hasFailure: ordered.some((run) => isFailedSubagentStatus(run.status)),
				},
			};
		})
		.sort((a, b) => a.sortKey.localeCompare(b.sortKey))
		.map((entry) => entry.group);
	// phase 付きの run はフェーズセクションにだけ出す (同じ run が 2 箇所に出ないようにする)。
	return { phases, ...splitSubagentRuns(standalone.filter((run) => !run.phase)) };
}

/**
 * 表示する run を決める。選択があれば一覧に行が無くても必ずそれを返す — 一覧が届いていない
 * (あるいは取得に失敗している) 間も、tool_result 由来の run id で実行履歴を開けるようにするため。
 * 一覧から run が消えることは無い (取得は全件で、チャット切替では選択ごと reset される) ので、
 * 一覧に無い選択は「まだ届いていない」だけ。ここで代わりの run に差し替えると、
 * SubagentTasksPane がその差し替えを store へ書き戻し、ユーザーのクリックが黙って捨てられる。
 * 選択が無いときだけ実行中を優先し、無ければ直近の実行済みを既定にする。
 */
export function resolveSelectedSubagentRunId(
	runs: ChatSubagentRun[],
	selectedId: string | null,
): string | null {
	if (selectedId !== null) return selectedId;
	const { running, finished } = splitSubagentRuns(runs);
	return running[0]?.id ?? finished[0]?.id ?? null;
}

/**
 * 導出した表示対象のうち、store へ書き戻すべき id。書き戻さないと実行中の run が終わるたびに
 * 別の run が導出され、読んでいる詳細から勝手に移動する (詳細は key で再マウントされ、展開・
 * スクロールも失う)。既に選択済み、または出せる run が無いときは書き戻さない (null)。
 */
export function subagentRunSelectionToCommit(
	runs: ChatSubagentRun[],
	selectedId: string | null,
): string | null {
	const shownId = resolveSelectedSubagentRunId(runs, selectedId);
	return shownId !== null && shownId !== selectedId ? shownId : null;
}
